import React from 'react';
import PropTypes from 'prop-types';
import { get, mapValues, pick } from 'lodash';
import * as Yup from 'yup';

import Grid from '~/components/core/Atomic/Grid/Grid';
import { useCms } from '~/components/hooks/useCms';
import useSafeFormikContext from '~/components/hooks/useSafeFormikContext';
import {
  activeFieldGroups,
  emptyFieldGroupFormikValues,
  fieldGroupValidations,
} from '~/components/IncidentConfiguration/HierarchyFieldGroup/hierarchyTree';
import CONFIGURABLE_LOBS from '~/server_shared/generated-types/CONFIGURABLE_LOBS';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import { useIncidentConfiguration } from '../../../../../hooks/useIncidentConfiguration';
import { useLob } from '../../../../../hooks/useLob';
import ConfiguredFields, {
  getConfiguredFieldsEmptyFormikInitialValues,
  getConfiguredFieldsValidations,
  getInitialSectionConfig,
  getPredefinedFieldsEmptyFormikInitialValues,
  PreDefinedField,
  preparePreDefinedFields,
} from '../../../../../IncidentConfiguration/ConfiguredFields';
import { getAdditionalDataValidations } from '../../../../../IncidentConfiguration/CustomFieldsContext';
import { usePolicy } from '../../../../../PolicyContainer';
import SectionFieldGroupPickers from '../../SectionFieldGroupPickers';

// The registry is shared with the backend. Every consumer that needs the *set* of these ids reads
// it — this module's config lookup and submit maps, create_like_draft_builder,
// fnol_api_to_fe_converter, auto_claims_manager and the sysconfig FnolConfiguration section — so
// the fields Create Like carries are the fields this section renders and an admin can configure.
// What a new registry entry still does not reach is the places that declare each field one by one,
// because every entry there differs: the <PreDefinedField id=...> list below (bespoke gridXs,
// disableFuture, a desc computed off the insured contact's occupation) and the per-field yup types
// in fnolConfigurationValidationSchema.
const SUPPLEMENTARY_BASE_FIELDS = CONFIGURABLE_LOBS.SUPPLEMENTARY_INFORMATION_FIELDS.base;

// A lob's fields and the pre_defined_fields group they are configured under come from one registry
// entry, so the two can never disagree about which lobs are covered.
const SUPPLEMENTARY_INFORMATION_LOB_SPECIFIC = CONFIGURABLE_LOBS.SUPPLEMENTARY_INFORMATION_FIELDS.lob_specific;
const SUPPLEMENTARY_INFORMATION_LOB_SPECIFIC_FIELDS = mapValues(SUPPLEMENTARY_INFORMATION_LOB_SPECIFIC, 'fields');
const LOB_ADDITIONAL_PATH = mapValues(SUPPLEMENTARY_INFORMATION_LOB_SPECIFIC, 'config_group');

const SUPPLEMENTARY_INFORMATION_PATH = 'incident_details.supplementary_information'; // Save those values together in the FE
const SUPPLEMENTARY_INFORMATION_CONFIG_PATH = 'incident_details.pre_defined_fields.supplementary_information';
const BASE_CONFIG_PATH = 'incident_details.pre_defined_fields';

// this function is the reflection of fnol_api_to_fe_converter.py::_extract_supplementary_information_values_from_incident_details in the backend, please change in tandem
const mapSupplementaryInformationToIncident = (values) =>
  pick(get(values, SUPPLEMENTARY_INFORMATION_PATH, {}), SUPPLEMENTARY_BASE_FIELDS);

// The auto-only half of the same section. These are AutoIncident columns, and the FNOL payload
// carries them one level down, under incident_details.auto_incident_additional — the shape
// AutoSpecificFnol.fill_lob_incident reads. Without this the section renders them, the user
// fills them in, and the submit drops them.
const mapAutoSupplementaryInformationToIncidentAdditional = (values, lob) =>
  pick(get(values, SUPPLEMENTARY_INFORMATION_PATH, {}), SUPPLEMENTARY_INFORMATION_LOB_SPECIFIC_FIELDS[lob] || []);

// TODO: NGTPA-15667 - should be in the config + migrate existing configs - not in the form itself
const getIsConfiguredFieldsActive = (incidentConfiguration) =>
  get(incidentConfiguration, 'incident_details.configured_fields', [])
    ?.filter((field) => field?.section === 'supplementary_info')
    ?.some((field) => field?.active || field?.mandatory);

// The hierarchy groups this section renders. Like the other helpers here it is a function of the
// configuration only, so the feature flag is read by the calling component and passed in. Show switched off
// hides them, as it hides a custom section's groups, and the configured-field fallback above that keeps a
// hidden section's fields on the form is not extended to them. An unset Show does not hide them.
const getSupplementaryInformationFieldGroups = (
  incidentConfiguration,
  additionalFieldOmitFn,
  isHierarchyFieldGroupsEnabled
) =>
  isHierarchyFieldGroupsEnabled && get(incidentConfiguration, SUPPLEMENTARY_INFORMATION_CONFIG_PATH)?.active !== false
    ? activeFieldGroups(
        incidentConfiguration,
        (fieldGroup) => (additionalFieldOmitFn ? additionalFieldOmitFn(fieldGroup) : true),
        'supplementary_info'
      )
    : [];

// A group counts only when the claim's sub-type keeps it, so a section whose only content is a group
// scoped to another sub-type neither renders nor demands verification.
const getIsSupplementaryInformationSectionActive = (
  incidentConfiguration,
  isHierarchyFieldGroupsEnabled = false,
  isFieldSupportedByCategories
) => {
  const isSupplementaryInformationSectionActive = get(
    incidentConfiguration,
    SUPPLEMENTARY_INFORMATION_CONFIG_PATH,
    {}
  )?.active;

  const isConfiguredFieldsActive = getIsConfiguredFieldsActive(incidentConfiguration);

  return (
    isSupplementaryInformationSectionActive ||
    isConfiguredFieldsActive ||
    getSupplementaryInformationFieldGroups(
      incidentConfiguration,
      isFieldSupportedByCategories,
      isHierarchyFieldGroupsEnabled
    ).length > 0
  );
};

const getSupplementaryInformationSectionConfig = (
  incidentConfiguration,
  isFieldSupportedByCategories,
  isHierarchyFieldGroupsEnabled = false
) => {
  const config = getInitialSectionConfig(
    incidentConfiguration,
    SUPPLEMENTARY_INFORMATION_CONFIG_PATH,
    { active: true, mandatory: true },
    isFieldSupportedByCategories
  );

  // A mandatory group blocks the FNOL the way it does in Incident Details: the section it renders in
  // must be saved with the group filled before the claim can be created.
  if (
    getSupplementaryInformationFieldGroups(
      incidentConfiguration,
      isFieldSupportedByCategories,
      isHierarchyFieldGroupsEnabled
    ).some((fieldGroup) => fieldGroup.mandatory)
  ) {
    config.mandatory = true;
  }

  return config;
};

const getSupplementaryInformationSectionTitle = (incidentConfiguration) =>
  get(getSupplementaryInformationSectionConfig(incidentConfiguration), 'desc', 'Supplementary Information');

const getSupplementaryInformationPredefinedFields = (incidentConfiguration, lob, additionalFieldOmitFn) => {
  const preDefinedFields = {};
  SUPPLEMENTARY_BASE_FIELDS.forEach((fieldId) => {
    preDefinedFields[fieldId] = get(incidentConfiguration, `${BASE_CONFIG_PATH}.${fieldId}`, {});
  });

  if (Object.keys(SUPPLEMENTARY_INFORMATION_LOB_SPECIFIC_FIELDS).includes(lob)) {
    SUPPLEMENTARY_INFORMATION_LOB_SPECIFIC_FIELDS[lob].forEach((fieldId) => {
      preDefinedFields[fieldId] = get(
        incidentConfiguration,
        `${BASE_CONFIG_PATH}.${LOB_ADDITIONAL_PATH[lob]}.${fieldId}`
      );
    });
  }

  return preparePreDefinedFields(preDefinedFields, additionalFieldOmitFn);
};

const getSupplementaryInformationInitialValues = (
  incidentConfiguration,
  lob,
  additionalFieldOmitFn,
  isHierarchyFieldGroupsEnabled = false
) => ({
  ...getPredefinedFieldsEmptyFormikInitialValues(
    getSupplementaryInformationPredefinedFields(incidentConfiguration, lob, additionalFieldOmitFn)
  ),
  configured_fields_values: {
    ...getConfiguredFieldsEmptyFormikInitialValues(
      get(incidentConfiguration, 'incident_details.configured_fields', []),
      'supplementary_info',
      additionalFieldOmitFn
    ),
    ...emptyFieldGroupFormikValues(
      getSupplementaryInformationFieldGroups(
        incidentConfiguration,
        additionalFieldOmitFn,
        isHierarchyFieldGroupsEnabled
      )
    ),
  },
});

const getSupplementaryInformationValidationSchema = (
  incidentConfiguration,
  lob,
  additionalFieldOmitFn,
  isHierarchyFieldGroupsEnabled = false
) => ({
  ...getAdditionalDataValidations(
    getSupplementaryInformationPredefinedFields(incidentConfiguration, lob, additionalFieldOmitFn)
  ),
  configured_fields_values: Yup.object().shape({
    ...getConfiguredFieldsValidations(incidentConfiguration, 'supplementary_info', additionalFieldOmitFn),
    ...fieldGroupValidations(
      getSupplementaryInformationFieldGroups(
        incidentConfiguration,
        additionalFieldOmitFn,
        isHierarchyFieldGroupsEnabled
      )
    ),
  }),
});

const SupplementaryInformationFragment = ({ additionalFieldOmitFn, claimId }) => {
  const { lob } = useLob();
  const { policy } = usePolicy();
  const { userOrganization } = useCms();
  const { incidentConfiguration } = useIncidentConfiguration();
  const preDefinedFields = getSupplementaryInformationPredefinedFields(
    incidentConfiguration,
    lob,
    additionalFieldOmitFn
  );
  const configurableFields = incidentConfiguration.incident_details.configured_fields.filter((field) =>
    additionalFieldOmitFn ? additionalFieldOmitFn(field) : true
  );
  const formikContext = useSafeFormikContext();
  const fieldGroups = getSupplementaryInformationFieldGroups(
    incidentConfiguration,
    additionalFieldOmitFn,
    isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.HIERARCHY_FIELD_GROUPS)
  );

  return (
    <>
      <PreDefinedField id="did_anyone_take_photos_or_video" fields={preDefinedFields} inline />
      <PreDefinedField id="was_cctv_footage" fields={preDefinedFields} inline />
      <PreDefinedField id="can_take_photos_of_damage" fields={preDefinedFields} inline />
      <PreDefinedField id="photographer_contact_id" fields={preDefinedFields} inline />
      <PreDefinedField id="is_other_policy_covering" fields={preDefinedFields} inline />
      <PreDefinedField id="other_policy_description" fields={preDefinedFields} inline />
      <PreDefinedField id="is_notification_only" fields={preDefinedFields} inline gridXs={12} />
      <PreDefinedField id="was_replacement_car_rented" fields={preDefinedFields} inline gridXs={12} />
      <PreDefinedField id="replacement_car_rental_date" fields={preDefinedFields} inline disableFuture />
      <PreDefinedField id="are_convictions_or_claims_in_the_last_5_years" fields={preDefinedFields} inline />
      <PreDefinedField id="convictions_or_claim_description" fields={preDefinedFields} inline gridXs={12} />
      <PreDefinedField
        id="is_policyholder_occupation_right"
        fields={preDefinedFields}
        desc={`Is current occupation '${policy?.insured_contact?.occupation || 'Not Found'}'?`}
        inline
        gridXs={12}
      />
      <PreDefinedField id="policyholder_current_occupation" fields={preDefinedFields} inline gridXs={12} />
      <PreDefinedField id="does_policyholder_second_occupation_exist" fields={preDefinedFields} inline gridXs={12} />
      <PreDefinedField id="policyholder_second_occupation" fields={preDefinedFields} inline gridXs={12} />
      <Grid item xs={12}>
        <ConfiguredFields
          values={formikContext ? formikContext.values : {}}
          customFields={configurableFields.filter((field) => field.section === 'supplementary_info')}
          inline
        />
      </Grid>
      <SectionFieldGroupPickers fieldGroups={fieldGroups} claimId={claimId} />
    </>
  );
};

SupplementaryInformationFragment.propTypes = {
  additionalFieldOmitFn: PropTypes.func,
  claimId: PropTypes.number,
};

export {
  getIsSupplementaryInformationSectionActive,
  getSupplementaryInformationFieldGroups,
  getSupplementaryInformationInitialValues,
  getSupplementaryInformationSectionConfig,
  getSupplementaryInformationSectionTitle,
  getSupplementaryInformationValidationSchema,
  mapAutoSupplementaryInformationToIncidentAdditional,
  mapSupplementaryInformationToIncident,
  SUPPLEMENTARY_INFORMATION_PATH,
  SupplementaryInformationFragment,
};
